/* eslint-disable max-len */
/* eslint-disable no-multiple-empty-lines */
/* eslint-disable import-helpers/order-imports */

import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { yupResolver } from '@hookform/resolvers/yup';

import { AppContext } from '../../../contexts/app';
import api from '../../../services/api';
import { buildReembolsoFormData, validateClienteNicash } from '../buildReembolsoFormData';
import {
  applyNicClienteAutoFill,
  mapUsersToClientesOptions,
} from '../clientesReembolso';
import ReembolsoFormFields from '../ReembolsoFormFields';
import { defaultFormValues, schema } from '../validationSchema';

function NicashReembolsoCreate(_props, ref) {
  const { setLoading, notificationRef } = useContext(AppContext);
  const [show, setShow] = useState(false);
  const [profissionalId, setProfissionalId] = useState(null);
  const [profissionalNome, setProfissionalNome] = useState('');
  const [onSaveCallback, setOnSaveCallback] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const {
    control,
    clearErrors,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema),
  });

  const responsavelTipo = watch('responsavelTipo');
  const formaPagamento = watch('formaPagamento');

  useEffect(() => {
    if (responsavelTipo === 'paciente') {
      setValue('cliente', '');
      clearErrors('cliente');
    }
  }, [clearErrors, responsavelTipo, setValue]);

  useEffect(() => {
    if (formaPagamento === 'reembolso') {
      setValue('ultimosNumerosCartao', '');
    }
  }, [formaPagamento, setValue]);

  useEffect(() => {
    if (!show) return undefined;

    const needsClientes = responsavelTipo === 'paciente'
      || responsavelTipo === 'nic_sp'
      || responsavelTipo === 'nic_go';

    if (!needsClientes) {
      setClientes([]);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoadingClientes(true);
        const { data = [] } = await api.get('/users?limit=0');
        if (cancelled) return;
        const clientesMapeados = mapUsersToClientesOptions(data);
        setClientes(clientesMapeados);

        if (responsavelTipo === 'nic_sp' || responsavelTipo === 'nic_go') {
          applyNicClienteAutoFill({
            responsavelTipo,
            clientes: clientesMapeados,
            setValue,
            setError,
            clearErrors,
            notificationRef,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          if (responsavelTipo === 'nic_sp' || responsavelTipo === 'nic_go') {
            setValue('cliente', '');
            setError('cliente', {
              type: 'manual',
              message: 'Não foi encontrado cliente para o responsável escolhido',
            });
            notificationRef.current.notify({
              message: 'Não foi encontrado cliente para o responsável escolhido',
              color: 'warning',
            });
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingClientes(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearErrors, notificationRef, responsavelTipo, setError, setValue, show]);

  const open = useCallback((row, initialValues, onSave) => {
    setProfissionalId(row?.profissionalId);
    setProfissionalNome(row?.especialista || '');
    setOnSaveCallback(() => onSave);
    reset({
      ...defaultFormValues,
      ...initialValues,
      hasExistingComprovante: false,
      isChangingComprovante: false,
      file: null,
    });
    clearErrors();
    setShow(true);
  }, [clearErrors, reset]);

  useImperativeHandle(ref, () => ({
    show: open,
    close: () => setShow(false),
  }), [open]);

  const handleClose = useCallback(() => {
    setShow(false);
    setProfissionalId(null);
    setProfissionalNome('');
  }, []);

  const onSubmit = useCallback((formValues) => {
    if (!profissionalId) {
      notificationRef.current.notify({
        message: 'Não foi possível identificar o profissional do reembolso.',
        color: 'danger',
      });
      return;
    }

    if (!validateClienteNicash(formValues, setError)) {
      return;
    }

    (async () => {
      try {
        setLoading({ loading: true, message: 'Cadastrando reembolso...' });
        const formData = buildReembolsoFormData(formValues);
        const { data } = await api.post(
          `/financeiroProfissionalReembolsos?profissionalId=${profissionalId}`,
          formData,
        );
        setLoading(false);
        notificationRef.current.notify({ message: 'Solicitação de reembolso realizada com sucesso' });
        handleClose();
        if (typeof onSaveCallback === 'function') {
          onSaveCallback(data);
        }
      } catch (error) {
        setLoading(false);
        notificationRef.current.notify({
          message: error?.response?.data?.message || 'Falha ao cadastrar reembolso',
          color: 'danger',
        });
      }
    })();
  }, [handleClose, notificationRef, onSaveCallback, profissionalId, setError, setLoading]);

  return (
    <Modal isOpen={show} centered toggle={handleClose}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          <strong>Clonar reembolso</strong>
          <Button
            className="close position-absolute top-4 right-4 py-1 px-2"
            onClick={handleClose}
          >
            <i className="fas fa-times" />
          </Button>
        </ModalHeader>
        <ModalBody>
          {profissionalNome && (
            <p className="text-muted mb-3">
              <strong>Especialista:</strong>
              {' '}
              {profissionalNome}
            </p>
          )}
          <ReembolsoFormFields
            control={control}
            errors={errors}
            responsavelTipo={responsavelTipo}
            formaPagamento={formaPagamento}
            clientes={clientes}
            loadingClientes={loadingClientes}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="success" type="submit">Solicitar</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default forwardRef(NicashReembolsoCreate);
